import { IconHash } from "@tabler/icons-react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import type { allChangelogs } from "content-collections";
import { useState } from "react";
import { ChangeLogCard } from "@/components/cards/change-log-card";
import { ChangelogTypeFilter } from "@/components/misc/ChangeLogFilter";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { config } from "@/data/config";
import {
	changelogVersionSectionClass,
	changelogVersionTitleClass,
} from "@/lib/changelog-type-styles";
import { isModuleEnabled } from "@/lib/modules";
import { cn } from "@/lib/utils";
import { getStaggeredAnimationClasses } from "@/utils/animation";
import {
	changelogsByVersion,
	sortedVersionsNewestFirst,
} from "@/utils/change-log";
import { seo } from "@/utils/seo";

const sortedVersions = sortedVersionsNewestFirst;
const limitedVersions = sortedVersions.slice(
	0,
	config.changelog.show_last_versions,
);

export const Route = createFileRoute("/_marketing/changelog/")({
	beforeLoad: () => {
		if (!isModuleEnabled("marketing_changelog")) {
			throw redirect({ to: "/" });
		}
	},
	component: RouteComponent,
	head: () =>
		seo({
			title: "Changelog",
			description:
				"Keep track of our latest features, improvements, and bug fixes.",
			keywords:
				"changelog, updates, changes, bug fixes, improvements, features",
		}),
});

export function RouteComponent() {
	const [selectedType, setSelectedType] = useState<string>("all");

	const filteredChangelogs = (changelogs: typeof allChangelogs) => {
		if (selectedType === "all") return changelogs;
		return changelogs.filter((changelog) => changelog.type === selectedType);
	};

	return (
		<MarketingShell>
			<section className="pt-20 pb-16 sm:pt-28 sm:pb-24">
				<MarketingContainer>
					<MarketingHeader
						title="Changelog"
						heading="Ship log for every release"
						description="Follow what changed, why it matters, and when it landed. Filter by change type when you only care about fixes or features."
					/>

					<div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
						<ChangelogTypeFilter
							selectedType={selectedType}
							onSelectType={setSelectedType}
						/>
					</div>

					<div className="flex flex-col gap-14 sm:gap-16">
						{limitedVersions.map((version, index) => {
							const filteredVersionChangelogs = filteredChangelogs(
								changelogsByVersion[version],
							);
							if (filteredVersionChangelogs.length === 0) return null;

							return (
								<div
									className={cn(
										getStaggeredAnimationClasses(index),
										changelogVersionSectionClass,
									)}
									key={version}
								>
									<Link
										to="/changelog/$version"
										params={{ version }}
										className={changelogVersionTitleClass}
									>
										<IconHash
											className="size-6 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100 md:block"
											aria-hidden
										/>
										{version}
									</Link>
									<div className="flex flex-col gap-3">
										{filteredVersionChangelogs
											.slice(0, config.changelog.show_updates_per_version)
											.map((changelog) => (
												<ChangeLogCard
													key={changelog._meta.path}
													changelog={changelog}
												/>
											))}
									</div>
									{filteredVersionChangelogs.length >
										config.changelog.show_updates_per_version && (
										<div className="mt-4">
											<Link
												to="/changelog/$version"
												params={{ version: version }}
												className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
											>
												View all {filteredVersionChangelogs.length} changes in
												version {version}
												<span aria-hidden>→</span>
											</Link>
										</div>
									)}
								</div>
							);
						})}
						{sortedVersions.length > config.changelog.show_last_versions && (
							<div className="border-t border-border/60 pt-8 text-center">
								<Link
									to="/changelog/archive"
									className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
								>
									View the archive of{" "}
									{sortedVersions.length - config.changelog.show_last_versions}{" "}
									older versions
									<span aria-hidden>→</span>
								</Link>
							</div>
						)}
					</div>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
